import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  FileStack, UploadCloud, Sparkles, Save, Trash2, Plus, ChevronLeft, ChevronRight,
  ArrowLeft, Link2, Paperclip, Loader2, Copy, Eye, X, Building2,
} from "lucide-react";

const FIELD_TYPES = [
  { v: "text", l: "Testo" }, { v: "number", l: "Numero" }, { v: "date", l: "Data" },
  { v: "yes_no", l: "Sì/No (una risposta)" },
  { v: "single_choice", l: "Scelta multipla (una risposta)" },
  { v: "checkbox", l: "Casella singola" }, { v: "multiline", l: "Testo lungo" },
];

export default function SignQuestionari() {
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(0);
  const [selFieldId, setSelFieldId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploadName, setUploadName] = useState("");
  const [uploadProduct, setUploadProduct] = useState("");
  const [preview, setPreview] = useState(false);

  const loadList = useCallback(async () => {
    try { const { data } = await api.get("/sign/forms"); setTemplates(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const openTemplate = async (id) => {
    try {
      const { data } = await api.get(`/sign/forms/${id}`);
      setSelected(data); setPage(0); setSelFieldId(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createTemplate = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", uploadName || file.name.replace(/\.pdf$/i, ""));
      fd.append("product", uploadProduct);
      const { data } = await api.post("/sign/forms", fd);
      toast.success("Questionario caricato");
      setUploadName(""); setUploadProduct("");
      await loadList();
      openTemplate(data.template_id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const autodetect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sign/forms/${selected.template_id}/autodetect`);
      setSelected((s) => ({ ...s, fields: data.fields }));
      toast.success(`${data.fields.length} campi proposti dall'AI — rivedili e salva`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/sign/forms/${selected.template_id}`, {
        name: selected.name, product: selected.product,
        fields: selected.fields, attachments: selected.attachments,
      });
      setSelected(data); toast.success("Questionario salvato"); await loadList();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const delTemplate = async (id) => {
    await api.delete(`/sign/forms/${id}`); await loadList();
    if (selected?.template_id === id) setSelected(null);
    toast.success("Questionario eliminato");
  };

  const updField = (id, patch) => setSelected((s) => ({ ...s, fields: s.fields.map((f) => f.id === id ? { ...f, ...patch } : f) }));
  const addField = () => {
    const id = `f_${Math.random().toString(36).slice(2, 10)}`;
    setSelected((s) => ({ ...s, fields: [...(s.fields || []), { id, label: "Nuovo campo", page, type: "text", x: 60, y: 60, w: 180, h: 16, required: false, section: "" }] }));
    setSelFieldId(id);
  };
  const delField = (id) => setSelected((s) => ({ ...s, fields: s.fields.filter((f) => f.id !== id) }));

  const addAttachment = () => setSelected((s) => ({ ...s, attachments: [...(s.attachments || []), { key: `att_${Math.random().toString(36).slice(2, 7)}`, label: "Nuovo allegato", required: false }] }));
  const updAttachment = (key, patch) => setSelected((s) => ({ ...s, attachments: s.attachments.map((a) => a.key === key ? { ...a, ...patch } : a) }));
  const delAttachment = (key) => setSelected((s) => ({ ...s, attachments: s.attachments.filter((a) => a.key !== key) }));

  const onImageClick = (e) => {
    if (!selFieldId || !selected) return;
    const pg = selected.pages[page]; if (!pg) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    updField(selFieldId, { x: Math.round(px * pg.w), y: Math.round(py * pg.h), page });
    toast.info("Posizione campo aggiornata");
  };

  // ------- LIST VIEW -------
  if (!selected) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="sign-questionari">
        <Header />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <button onClick={() => nav("/assietica-sign")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="q-back"><ArrowLeft className="h-4 w-4" /> Assietica Sign</button>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><FileStack className="h-6 w-6" /></div>
            <div><h1 className="font-heading text-2xl font-semibold text-slate-900">Questionari (Web Form)</h1><p className="text-slate-500 text-sm">Trasforma i modelli della Compagnia in moduli compilabili dal cliente</p></div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="q-upload-box">
            <h3 className="font-semibold text-slate-800 mb-3">Carica un nuovo questionario (PDF originale)</h3>
            <div className="grid sm:grid-cols-3 gap-3 mb-3">
              <Input placeholder="Nome (es. Questionario D&O)" value={uploadName} onChange={(e) => setUploadName(e.target.value)} data-testid="q-upload-name" />
              <Input placeholder="Prodotto (es. D&O, RC Economia Solidale)" value={uploadProduct} onChange={(e) => setUploadProduct(e.target.value)} data-testid="q-upload-product" />
              <label className="inline-flex items-center justify-center gap-2 h-10 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-sm cursor-pointer px-4" data-testid="q-upload-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} Carica PDF
                <input type="file" accept="application/pdf" className="hidden" onChange={(e) => createTemplate(e.target.files?.[0])} />
              </label>
            </div>
          </div>

          <StaticSend />

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t) => (
              <div key={t.template_id} className="rounded-xl border border-slate-200 bg-white p-4 hover:border-sky-300 transition-colors" data-testid={`q-card-${t.template_id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <p className="text-xs uppercase text-sky-600 font-semibold">{t.product || "—"}</p>
                    <h3 className="font-semibold text-slate-800 truncate">{t.name}</h3>
                    <p className="text-xs text-slate-400 mt-1">{t.pages?.length || 0} pagine · {t.fields?.length || 0} campi</p>
                  </div>
                  <button onClick={() => delTemplate(t.template_id)} className="text-slate-300 hover:text-red-500" data-testid={`q-del-${t.template_id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <Button onClick={() => openTemplate(t.template_id)} variant="outline" className="w-full mt-3 rounded-md" data-testid={`q-open-${t.template_id}`}>Apri e configura</Button>
              </div>
            ))}
            {templates.length === 0 && <p className="text-sm text-slate-400 col-span-full">Nessun questionario. Carica il primo modello PDF.</p>}
          </div>

          <RequestsPanel />
        </main>
      </div>
    );
  }

  // ------- EDITOR VIEW -------
  const pg = selected.pages[page] || { w: 595, h: 842 };
  const pageFields = (selected.fields || []).filter((f) => f.page === page);
  return (
    <div className="min-h-screen bg-slate-50" data-testid="sign-questionari-editor">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4" data-testid="q-editor-back"><ArrowLeft className="h-4 w-4" /> Tutti i questionari</button>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <Input value={selected.name} onChange={(e) => setSelected({ ...selected, name: e.target.value })} className="max-w-xs font-semibold" data-testid="q-edit-name" />
          <Input value={selected.product || ""} onChange={(e) => setSelected({ ...selected, product: e.target.value })} placeholder="Prodotto" className="max-w-[200px]" data-testid="q-edit-product" />
          <Button onClick={() => setPreview(true)} disabled={busy} variant="outline" className="rounded-md" data-testid="q-preview"><Eye className="h-4 w-4 mr-1.5" /> Anteprima</Button>
          <Button onClick={autodetect} disabled={busy} variant="outline" className="rounded-md" data-testid="q-autodetect"><Sparkles className="h-4 w-4 mr-1.5" /> {busy ? "Analisi…" : "Autodetect AI"}</Button>
          <Button onClick={saveTemplate} disabled={busy} className="rounded-md bg-sky-600 hover:bg-sky-700 text-white" data-testid="q-save"><Save className="h-4 w-4 mr-1.5" /> Salva</Button>
        </div>

        <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
          {/* PDF preview + overlay */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-slate-500">Pagina {page + 1} di {selected.pages.length} {selFieldId && <span className="text-sky-600">· clicca sul modello per posizionare il campo selezionato</span>}</p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="q-page-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" disabled={page >= selected.pages.length - 1} onClick={() => setPage((p) => p + 1)} data-testid="q-page-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
            <div className="relative border border-slate-300 rounded-md overflow-hidden bg-white" onClick={onImageClick} style={{ cursor: selFieldId ? "crosshair" : "default" }} data-testid="q-page-canvas">
              <img src={`${API}/sign/forms/${selected.template_id}/page/${page}.png`} alt={`pagina ${page + 1}`} className="w-full block select-none" draggable={false} />
              {pageFields.map((f) => (
                <div key={f.id} onClick={(e) => { e.stopPropagation(); setSelFieldId(f.id); }}
                  className={`absolute border-2 text-[9px] leading-none px-0.5 ${selFieldId === f.id ? "border-sky-500 bg-sky-500/20" : "border-emerald-500 bg-emerald-500/10"}`}
                  style={{ left: `${(f.x / pg.w) * 100}%`, top: `${(f.y / pg.h) * 100}%`, width: `${(f.w / pg.w) * 100}%`, height: `${Math.max((f.h / pg.h) * 100, 1.5)}%` }}
                  title={f.label} data-testid={`q-box-${f.id}`}>
                  <span className="text-emerald-800 truncate block">{f.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Fields + attachments panel */}
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800">Campi</h3>
                <Button size="sm" variant="outline" onClick={addField} data-testid="q-add-field"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
              <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                {(selected.fields || []).map((f) => (
                  <div key={f.id} className={`rounded-md border p-2 ${selFieldId === f.id ? "border-sky-400 bg-sky-50" : "border-slate-200"}`} onClick={() => { setSelFieldId(f.id); setPage(f.page); }} data-testid={`q-field-${f.id}`}>
                    <div className="flex items-center gap-2">
                      <Input value={f.label} onChange={(e) => updField(f.id, { label: e.target.value })} className="h-8 text-sm" data-testid={`q-field-label-${f.id}`} />
                      <button onClick={(e) => { e.stopPropagation(); delField(f.id); }} className="text-slate-300 hover:text-red-500 shrink-0" data-testid={`q-field-del-${f.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-xs">
                      <select value={f.type} onChange={(e) => updField(f.id, { type: e.target.value })} className="border rounded px-1 py-1 bg-white" data-testid={`q-field-type-${f.id}`}>
                        {FIELD_TYPES.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
                      </select>
                      <span className="text-slate-400">p.{f.page + 1}</span>
                      <span className="text-slate-400">x{Math.round(f.x)} y{Math.round(f.y)}</span>
                      <label className="flex items-center gap-1 ml-auto text-slate-500"><Checkbox checked={!!f.required} onCheckedChange={(v) => updField(f.id, { required: !!v })} /> obblig.</label>
                    </div>
                    {f.type === "single_choice" && (
                      <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()} data-testid={`q-opts-${f.id}`}>
                        <div className="text-[11px] text-slate-500">Opzioni (una risposta possibile). x,y = quadratino di ciascuna opzione.</div>
                        {(f.options || []).map((o, oi) => (
                          <div key={oi} className="flex items-center gap-1 text-[11px]">
                            <input value={o.label} onChange={(e) => updField(f.id, { options: f.options.map((x, k) => k === oi ? { ...x, label: e.target.value } : x) })} className="border rounded px-1 py-0.5 flex-1" placeholder="Etichetta" data-testid={`q-opt-label-${f.id}-${oi}`} />
                            <span>x</span><input type="number" value={Math.round(o.x || 0)} onChange={(e) => updField(f.id, { options: f.options.map((x, k) => k === oi ? { ...x, x: parseFloat(e.target.value) || 0 } : x) })} className="border rounded w-14 px-1 py-0.5" />
                            <span>y</span><input type="number" value={Math.round(o.y || 0)} onChange={(e) => updField(f.id, { options: f.options.map((x, k) => k === oi ? { ...x, y: parseFloat(e.target.value) || 0 } : x) })} className="border rounded w-14 px-1 py-0.5" />
                            <button onClick={() => updField(f.id, { options: f.options.filter((_, k) => k !== oi) })} className="text-slate-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>
                          </div>
                        ))}
                        <button onClick={() => updField(f.id, { options: [...(f.options || []), { label: "Nuova opzione", x: f.x, y: f.y }] })} className="text-[11px] text-sky-600 hover:underline" data-testid={`q-opt-add-${f.id}`}>+ Aggiungi opzione</button>
                      </div>
                    )}
                    {f.type === "yes_no" && (
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-500" onClick={(e) => e.stopPropagation()}>
                        <span className="shrink-0">Casella "No": x</span>
                        <input type="number" value={Math.round(f.x_no ?? (f.x + 60))} onChange={(e) => updField(f.id, { x_no: parseFloat(e.target.value) || 0 })} className="border rounded w-16 px-1 py-0.5" data-testid={`q-field-xno-${f.id}`} />
                        <span>y</span>
                        <input type="number" value={Math.round(f.y_no ?? f.y)} onChange={(e) => updField(f.id, { y_no: parseFloat(e.target.value) || 0 })} className="border rounded w-16 px-1 py-0.5" data-testid={`q-field-yno-${f.id}`} />
                        <span className="text-slate-400">(il riquadro verde = casella "Sì")</span>
                      </div>
                    )}
                  </div>
                ))}
                {(selected.fields || []).length === 0 && <p className="text-xs text-slate-400">Nessun campo. Usa "Autodetect AI" o aggiungi manualmente.</p>}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-800 flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Allegati richiesti</h3>
                <Button size="sm" variant="outline" onClick={addAttachment} data-testid="q-add-att"><Plus className="h-4 w-4 mr-1" /> Aggiungi</Button>
              </div>
              <div className="space-y-2">
                {(selected.attachments || []).map((a) => (
                  <div key={a.key} className="flex items-center gap-2" data-testid={`q-att-${a.key}`}>
                    <Input value={a.label} onChange={(e) => updAttachment(a.key, { label: e.target.value })} className="h-8 text-sm" />
                    <label className="flex items-center gap-1 text-xs text-slate-500 shrink-0"><Checkbox checked={!!a.required} onCheckedChange={(v) => updAttachment(a.key, { required: !!v })} /> obblig.</label>
                    <button onClick={() => delAttachment(a.key)} className="text-slate-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                {(selected.attachments || []).length === 0 && <p className="text-xs text-slate-400">Es. Visura Camerale, Ultimo Bilancio, Codice Fiscale.</p>}
              </div>
            </div>

            <CreateRequest templateId={selected.template_id} />
          </div>
        </div>
        {preview && <FormPreview template={selected} onClose={() => setPreview(false)} />}
      </main>
    </div>
  );
}

function FormPreview({ template, onClose }) {
  const [values, setValues] = useState({});
  const setVal = (id, v) => setValues((p) => ({ ...p, [id]: v }));
  const fields = template.fields || [];
  const sections = [...new Set(fields.map((f) => f.section || "Dati"))];
  const _norm = (s) => (s || "").toLowerCase();
  const _findId = (keys) => (fields.find((f) => (!f.type || f.type === "text" || f.type === "number") && keys.some((k) => _norm(f.label).includes(k))) || {}).id;
  const pivaFieldId = _findId(["partita iva", "p. iva", "p.iva", "piva"]);
  const ragioneFieldId = _findId(["ragione sociale", "denominazione", "contraente", "azienda"]);
  const comuneFieldId = _findId(["comune"]);
  const [pivaBusy, setPivaBusy] = useState(false);
  const lookupPiva = async (piva) => {
    if (!piva || !piva.trim()) { toast.error("Inserisci prima la Partita IVA"); return; }
    setPivaBusy(true);
    try {
      const { data: r } = await api.get(`/sign/forms/lookup-piva`, { params: { piva: piva.trim() } });
      if (!r.disponibile) { toast.error(r.motivo || "Dati camerali non disponibili"); return; }
      setValues((p) => {
        const n = { ...p };
        if (ragioneFieldId && r.ragione_sociale) n[ragioneFieldId] = r.ragione_sociale;
        if (comuneFieldId && r.comune) n[comuneFieldId] = r.comune;
        return n;
      });
      toast.success(`Compilato: ${r.ragione_sociale}${r.comune ? " · " + r.comune : ""}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Verifica non riuscita"); }
    finally { setPivaBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-auto py-8 px-4" data-testid="q-preview-modal" onClick={onClose}>
      <div className="bg-slate-50 rounded-2xl max-w-2xl w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b bg-white rounded-t-2xl">
          <div className="text-sm font-semibold text-slate-700">Anteprima — come la vede il cliente</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="q-preview-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <p className="text-xs uppercase tracking-wide text-sky-600 font-semibold">{template.product || "Questionario"}</p>
            <h2 className="font-heading text-xl font-semibold text-slate-800">{template.name}</h2>
            <p className="text-sm text-slate-500 mt-1">Compila i campi richiesti. Al termine il cliente firma con OTP e documento d'identità.</p>
          </div>
          {sections.map((sec) => (
            <div key={sec} className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">{sec}</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {fields.filter((f) => (f.section || "Dati") === sec).map((f) => (
                  <div key={f.id} className={f.type === "multiline" || f.type === "single_choice" ? "sm:col-span-2" : ""}>
                    {f.type === "single_choice" ? (
                      <div>
                        <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                        <div className="flex flex-col gap-1.5">
                          {(f.options || []).map((o, oi) => (
                            <label key={oi} className="flex items-center gap-1.5 text-sm text-slate-700"><input type="radio" name={`pv-${f.id}`} checked={values[f.id] === o.label} onChange={() => setVal(f.id, o.label)} /> {o.label}</label>
                          ))}
                          {(f.options || []).length === 0 && <span className="text-xs text-amber-600">Nessuna opzione definita</span>}
                        </div>
                      </div>
                    ) : f.type === "yes_no" ? (
                      <div>
                        <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-1.5 text-sm"><input type="radio" name={`pv-${f.id}`} checked={values[f.id] === "si"} onChange={() => setVal(f.id, "si")} /> Sì</label>
                          <label className="flex items-center gap-1.5 text-sm"><input type="radio" name={`pv-${f.id}`} checked={values[f.id] === "no"} onChange={() => setVal(f.id, "no")} /> No</label>
                        </div>
                      </div>
                    ) : f.type === "checkbox" ? (
                      <label className="flex items-center gap-2 text-sm text-slate-700 mt-1"><Checkbox checked={values[f.id] === true} onCheckedChange={(v) => setVal(f.id, !!v)} /> <span>{f.label}{f.required && <span className="text-red-500"> *</span>}</span></label>
                    ) : (
                      <>
                        <label className="text-sm text-slate-600 mb-1 block">{f.label}{f.required && <span className="text-red-500"> *</span>}</label>
                        {f.type === "multiline"
                          ? <textarea rows={3} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} className="border rounded w-full p-2 text-sm" />
                          : <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} value={values[f.id] || ""} onChange={(e) => setVal(f.id, e.target.value)} />}
                        {f.id === pivaFieldId && (ragioneFieldId || comuneFieldId) && (
                          <button type="button" onClick={() => lookupPiva(values[f.id])} disabled={pivaBusy}
                            className="mt-1.5 inline-flex items-center gap-1.5 text-xs font-medium text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="q-piva-lookup">
                            {pivaBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Building2 className="h-3.5 w-3.5" />} Verifica P.IVA e compila i dati impresa
                          </button>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {fields.filter((f) => (f.section || "Dati") === sec).length === 0 && <p className="text-xs text-slate-400 sm:col-span-2">Nessun campo in questa sezione.</p>}
              </div>
            </div>
          ))}
          {(template.attachments || []).length > 0 && (
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Documenti da allegare</h3>
              {(template.attachments || []).map((a) => (
                <div key={a.key} className="flex items-center justify-between py-1 text-sm text-slate-600">
                  <span>{a.label}{a.required && <span className="text-red-500"> *</span>}</span>
                  <span className="text-sky-600 text-xs">Carica</span>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-slate-400 text-center">Anteprima non inviabile — solo visualizzazione.</p>
        </div>
      </div>
    </div>
  );
}

function CreateRequest({ templateId }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [needCompliance, setNeedCompliance] = useState(null);

  const search = async (term) => {
    setQ(term); setPicked(null);
    if (term.length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/sign/identities?q=${encodeURIComponent(term)}`); setResults((data.identities || data || []).slice(0, 6)); } catch { /* noop */ }
  };

  const create = async (confirmCompliance = false) => {
    if (!picked) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sign/forms/${templateId}/requests`, { identity_id: picked.identity_id, compliance_confirmed: confirmCompliance });
      const url = `${window.location.origin}${data.form_path}`;
      setLink(url); setNeedCompliance(null); toast.success("Link di compilazione creato");
    } catch (e) {
      const det = e.response?.data?.detail;
      if (e.response?.status === 409 && det?.code === "compliance_required") setNeedCompliance(det);
      else toast.error(formatApiErrorDetail(det));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-sky-200 bg-sky-50/50 p-4" data-testid="q-create-request">
      <h3 className="font-semibold text-slate-800 flex items-center gap-1.5 mb-3"><Link2 className="h-4 w-4" /> Genera link per il cliente</h3>
      <Input placeholder="Cerca cliente (nome, ragione sociale)…" value={q} onChange={(e) => search(e.target.value)} data-testid="q-req-search" />
      {results.length > 0 && !picked && (
        <div className="mt-2 border border-slate-200 rounded-md bg-white divide-y">
          {results.map((r) => (
            <button key={r.identity_id} onClick={() => { setPicked(r); setResults([]); setQ(r.display_name || r.ragione_sociale || `${r.nome} ${r.cognome}`); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`q-req-pick-${r.identity_id}`}>
              {r.display_name || r.ragione_sociale || `${r.nome} ${r.cognome || ""}`}
            </button>
          ))}
        </div>
      )}
      {picked && <p className="text-xs text-emerald-700 mt-2">Cliente: {picked.display_name || picked.ragione_sociale || `${picked.nome} ${picked.cognome || ""}`}</p>}
      {needCompliance && (
        <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="q-compliance-alert">
          <p className="text-xs text-amber-800">{needCompliance.message}</p>
          <Button onClick={() => create(true)} disabled={busy} size="sm" className="mt-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white" data-testid="q-compliance-confirm">Confermo la verifica con la Compagnia e procedo</Button>
        </div>
      )}
      <Button onClick={() => create(false)} disabled={!picked || busy} className="w-full mt-3 rounded-md bg-sky-600 hover:bg-sky-700 text-white" data-testid="q-req-create">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />} Crea link di compilazione</Button>
      {link && (
        <div className="mt-3 flex items-center gap-2 bg-white border border-slate-200 rounded-md p-2" data-testid="q-req-link">
          <span className="text-xs text-slate-600 truncate flex-1">{link}</span>
          <button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copiato"); }} className="text-sky-600 hover:text-sky-700 shrink-0"><Copy className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

function StaticSend() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [level, setLevel] = useState("OTP");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async (term) => {
    setQ(term); setPicked(null);
    if (term.length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/sign/identities?q=${encodeURIComponent(term)}`); setResults((data.identities || data || []).slice(0, 6)); } catch { /* noop */ }
  };

  const send = async () => {
    if (!picked || !file) { toast.error("Seleziona cliente e PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("identity_id", picked.identity_id);
      fd.append("title", title); fd.append("verification_level", level);
      const { data } = await api.post("/sign/forms/static-send", fd);
      setLink(`${window.location.origin}${data.sign_path}`);
      toast.success("Documento inviato alla firma");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mb-6" data-testid="static-send">
      <h3 className="font-semibold text-slate-800 flex items-center gap-1.5 mb-1"><FileStack className="h-4 w-4" /> Invio diretto documento statico</h3>
      <p className="text-sm text-slate-500 mb-3">Per MUP, informative privacy, quietanze e ricevute: invia il PDF direttamente alla firma (presa visione/accettazione), senza Web Form.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Input placeholder="Cerca cliente…" value={q} onChange={(e) => search(e.target.value)} data-testid="static-search" />
          {results.length > 0 && !picked && (
            <div className="mt-1 border border-slate-200 rounded-md bg-white divide-y">
              {results.map((r) => (
                <button key={r.identity_id} onClick={() => { setPicked(r); setResults([]); setQ(r.display_name || r.ragione_sociale || `${r.nome} ${r.cognome}`); }} className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50" data-testid={`static-pick-${r.identity_id}`}>
                  {r.display_name || r.ragione_sociale || `${r.nome} ${r.cognome || ""}`}
                </button>
              ))}
            </div>
          )}
        </div>
        <Input placeholder="Titolo (es. Informativa Privacy)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="static-title" />
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-3">
        <label className="inline-flex items-center gap-2 h-9 rounded-md border border-slate-300 px-3 text-sm cursor-pointer" data-testid="static-file">
          <UploadCloud className="h-4 w-4" /> {file ? file.name : "Scegli PDF"}
          <input type="file" accept="application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0])} />
        </label>
        <select value={level} onChange={(e) => setLevel(e.target.value)} className="border rounded-md px-2 py-2 text-sm bg-white" data-testid="static-level">
          <option value="OTP">Firma OTP (SMS)</option>
          <option value="OTP_ID">OTP + documento d'identità</option>
        </select>
        <Button onClick={send} disabled={busy} className="rounded-md bg-slate-900 hover:bg-slate-800 text-white" data-testid="static-send-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Link2 className="h-4 w-4 mr-1.5" />} Invia alla firma</Button>
      </div>
      {link && <div className="mt-3 flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-md p-2" data-testid="static-link"><span className="text-xs text-slate-600 truncate flex-1">{link}</span><button onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copiato"); }} className="text-sky-600 shrink-0"><Copy className="h-4 w-4" /></button></div>}
    </div>
  );
}

function RequestsPanel() {
  const [reqs, setReqs] = useState([]);
  const [pol, setPol] = useState({});
  const load = useCallback(async () => {
    try { const { data } = await api.get("/sign/forms/requests"); setReqs(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setPolizza = async (rid) => {
    const numero = (pol[rid] || "").trim();
    if (!numero) { toast.error("Inserisci il numero polizza/proposta"); return; }
    try {
      await api.post(`/sign/forms/requests/${rid}/polizza`, { numero_polizza: numero, tipo: "polizza" });
      toast.success("Numero polizza registrato — questionario pronto per l'iter statico");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!reqs.length) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mt-6" data-testid="requests-panel">
      <h3 className="font-semibold text-slate-800 mb-3">Richieste inviate</h3>
      <div className="space-y-2">
        {reqs.slice(0, 20).map((r) => (
          <div key={r.request_id} className="flex flex-wrap items-center gap-2 border-b border-slate-100 py-2 text-sm" data-testid={`req-row-${r.request_id}`}>
            <span className="font-medium text-slate-700 min-w-[160px] truncate">{r.identity_name}</span>
            <span className="text-xs text-slate-400">{r.product || "—"}</span>
            <span className={`text-xs px-2 py-0.5 rounded-sm ${r.status === "compilato" ? "bg-emerald-50 text-emerald-700" : r.status === "polizza_emessa" ? "bg-sky-50 text-sky-700" : "bg-slate-100 text-slate-500"}`}>{r.numero_polizza ? `Polizza ${r.numero_polizza}` : r.status}</span>
            {r.status === "compilato" && !r.numero_polizza && (
              <div className="flex items-center gap-1 ml-auto">
                <Input placeholder="N° polizza/proposta" value={pol[r.request_id] || ""} onChange={(e) => setPol((p) => ({ ...p, [r.request_id]: e.target.value }))} className="h-8 w-44 text-sm" data-testid={`req-polizza-input-${r.request_id}`} />
                <Button size="sm" variant="outline" onClick={() => setPolizza(r.request_id)} data-testid={`req-polizza-btn-${r.request_id}`}>Emessa</Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
